import { MultiEnvLogger } from './MultiEnvLogger';

export enum EndPoint {
  HEALIX_UI = 'healix-ui',
  HEALIX_API = 'healix-api',
  VERID_UI = 'verid-ui',
  VERID_API = 'verid-api',
}
export enum Role {
  ADMIN = 'ADMIN',
  STAFF = 'STAFF',
  USER = 'USER',
}

const DEBUG = false;

function roleInUrlToRole(role: string): Role {
  if (role === 'admin' || role === 'verid-admin' || role === 'ADMIN') {
    return Role.ADMIN;
  } else if (role === 'staff' || role === 'verid-staff' || role === 'STAFF') {
    return Role.STAFF;
  } else if (role === 'public' || role === 'user' || role === 'verid-public' || role === 'USER') {
    return Role.USER;
  } else {
    throw new Error(`Invalid role in URL: ${role}`);
  }
}

function roleToPathSegment(role: Role, endpoint: EndPoint): string {
  switch (endpoint) {
    case 'healix-ui':
    case 'healix-api':
      switch (role) {
        case 'ADMIN':
          return 'admin';
        case 'STAFF':
          return 'staff';
        case 'USER':
          return 'public'; // 'user' is not used in the URL, we use 'public' instead
        default:
          throw new Error(`Invalid role: ${role}`);
      }
    case 'verid-ui':
    case 'verid-api':
      switch (role) {
        case 'ADMIN':
          return 'verid-admin';
        case 'STAFF':
          return 'verid-staff';
        case 'USER':
          return 'verid-public'; // 'user' is not used in the URL, we use 'verid-public' instead
        default:
          throw new Error(`Invalid role: ${role}`);
      }
    default:
      throw new Error(`Invalid endpoint: ${endpoint}`);
  }
}

export function getPartsFromUrl(url: string): {
  role: Role | null;
  orgId: string | null;
  useQueryStringForOrgAndRole: boolean;
  queryParams?: string;
} {
  // Example inpnuts:
  // local dev: http://localhost:3000/whatEver # Missing return nulls
  // Cloud: https://public.develop.healix.cmsmastercloud.ca/whatEver return # role indiator is public, orgId is develop

  // Must run on client or server
  const protocol = url.split('://')[0];
  const baseUrl = url.split('://')[1].split('/')[0];
  const queryParams = url.split('?')[1];
  const isLocalDevOrOldInfra = baseUrl.indexOf('localhost') !== -1 || baseUrl.indexOf('citizensupport.app') !== -1;
  DEBUG && MultiEnvLogger.debug('getPartsFromUrl', '', { url, protocol, baseUrl, queryParams, isLocalDevOrOldInfra });
  if (isLocalDevOrOldInfra) {
    // return the role and orgId from the URL params
    const urlParams = new URLSearchParams(queryParams);
    const stringRole = urlParams.get('role');
    const typedRole = stringRole ? roleInUrlToRole(stringRole) : null;
    const orgId = urlParams.get('orgId');
    DEBUG &&
      MultiEnvLogger.debug('getPartsFromUrl', 'isLocalDevOrOldInfra', {
        stringRole,
        typedRole,
        orgId,
      });
    return {
      orgId: orgId || null,
      role: typedRole,
      useQueryStringForOrgAndRole: true,
      queryParams: queryParams || undefined,
    };
  }

  // must be cloud
  const parts = baseUrl.split('.');
  const stringRole = parts[0];
  const typedRole = roleInUrlToRole(stringRole);
  const orgid = parts[1];
  if (!orgid || !typedRole) {
    throw new Error(`Invalid URL format. Expected format: ${protocol}://role.orgId.BASE_DOMAIN`);
  }
  DEBUG &&
    MultiEnvLogger.debug('getPartsFromUrl', 'isCloud', {
      stringRole,
      typedRole,
      orgid,
    });

  return {
    role: typedRole,
    orgId: orgid,
    useQueryStringForOrgAndRole: false,
    queryParams: queryParams || '',
  };
}

function orgAndRoleQueryParams({
  role,
  orgId,
  queryParams,
  endPoint,
}: {
  role: Role | null;
  orgId: string | null;
  queryParams?: string;
  endPoint: EndPoint;
}): string {
  if (!role || !orgId) {
    return queryParams || '';
  }
  return `?role=${roleToPathSegment(role, endPoint)}&orgId=${orgId}${queryParams ? `&${queryParams}` : ''}`;
}

export function getRelativeEndPointUrl(url: string, endPoint: EndPoint): string {
  const { role, orgId, queryParams, useQueryStringForOrgAndRole } = getPartsFromUrl(url);
  if (useQueryStringForOrgAndRole) {
    // Local development or old infra, return the URL with the role and orgId
    switch (endPoint) {
      case 'healix-ui':
        return `http:localhost:3000/${orgAndRoleQueryParams({ role, orgId, queryParams, endPoint })}`;
      case 'healix-api':
        return `http:localhost:7071/graphql?${orgAndRoleQueryParams({ role, orgId, queryParams, endPoint })}`;
      case 'verid-ui':
        return `http:localhost:3004/${orgAndRoleQueryParams({ role, orgId, queryParams, endPoint })}`;
      case 'verid-api':
        return `http:localhost:7072/graphql?${orgAndRoleQueryParams({ role, orgId, queryParams, endPoint })}`;
      default:
        throw new Error(`Invalid endpoint: ${endPoint}`);
    }
  }
  if (!orgId) {
    throw new Error('Cannot get relative endpoint URL without orgId');
  }
  if (!role) {
    throw new Error('Cannot get relative endpoint URL without role');
  }
  switch (endPoint) {
    case 'healix-ui':
      return `https://${roleToPathSegment(role, endPoint)}.${orgId}.healix.cmsmastercloud.ca`;
    case 'healix-api':
      return `https://${roleToPathSegment(role, endPoint)}.${orgId}.healix.cmsmastercloud.ca/graphql`;
    case 'verid-ui':
      return `https://${roleToPathSegment(role, endPoint)}.${orgId}.healix.cmsmastercloud.ca`;
    case 'verid-api':
      return `https://${roleToPathSegment(role, endPoint)}.${orgId}.healix.cmsmastercloud.ca/graphql`;
    default:
      throw new Error(`Invalid endpoint: ${endPoint}`);
  }
}
